import styled from 'styled-components';
import { colors, radii, spacing, typography } from '../../../theme-v2';

export interface ReferenceCardProps {
  /** The cited excerpt. Clamped to 5 lines. */
  quote: string;
  /** Pre-formatted page label, e.g. "Page 4". */
  pageLabel?: string;
  /** Document name — shown only when the source is from another document. */
  documentName?: string;
  /** Highlighted state after the card was clicked. */
  active?: boolean;
  onClick?: () => void;
}

/**
 * ReferenceCard — one chatbot source citation. Shows the excerpt (5-line
 * clamp) and a page label; when the source is from a document other than the
 * one displayed, its document name is shown beneath. Clicking scrolls the
 * document to the cited passage (wired by the parent).
 */
export const ReferenceCard = ({
  quote,
  pageLabel,
  documentName,
  active,
  onClick,
}: ReferenceCardProps) => (
  <StyledCard type="button" $active={Boolean(active)} onClick={onClick}>
    <StyledQuote>{quote}</StyledQuote>
    <StyledFooter>
      {pageLabel ? (
        <StyledPage $active={Boolean(active)}>{pageLabel}</StyledPage>
      ) : null}
      {documentName ? <StyledDocName>{documentName}</StyledDocName> : null}
    </StyledFooter>
  </StyledCard>
);

const StyledCard = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${spacing.xs}px;
  text-align: left;
  height: 100%;
  padding: ${spacing.sm}px;
  background: ${colors.surface.muted};
  border: 1px solid
    ${({ $active }) => ($active ? colors.accent.base : 'transparent')};
  border-radius: ${radii.box}px;
  cursor: pointer;
  font-family: ${typography.fontFamily};
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${colors.accent.base};
  }
`;

const StyledQuote = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: ${typography.size.sm}px;
  line-height: ${typography.lineHeight.tight};
  color: ${colors.text.primary};
`;

const StyledFooter = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledPage = styled.span<{ $active: boolean }>`
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${({ $active }) =>
    $active ? colors.accent.base : colors.text.secondary};
`;

const StyledDocName = styled.span`
  font-size: ${typography.size.xs}px;
  font-style: italic;
  color: ${colors.text.tertiary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
